/**
 * ReportItemsStep
 *
 * Displays a scrollable list of user report items with a chevron on the right.
 * Used for each drill-down level in the ReportUserSheet stepper.
 *
 * Matches Flutter implementation:
 * lib/presentation/bottom_sheet/report_user_bottom_sheet.dart (actionItem widget)
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { UserReportItemEntity } from '@/domain/entities/report/userReportItemEntity';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { ForwardChevronIcon } from '@/presentation/components/common/ForwardChevronIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ReportItemsStepProps {
  items: UserReportItemEntity[];
  isLoading: boolean;
  onItemTap: (item: UserReportItemEntity) => void;
}

export const ReportItemsStep: React.FC<ReportItemsStepProps> = ({
  items,
  isLoading,
  onItemTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: responsive.spacing['3xl'],
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  return (
    <div
      style={{
        overflowY: 'auto',
        height: '100%',
        paddingLeft: responsive.spacing.xl,
        paddingRight: responsive.spacing.xl,
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {items.map((item) => (
        <div
          key={item.id}
          {...qaAttr({ id: `report-user-reason-${item.id}` })}
          onClick={() => onItemTap(item)}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            paddingBottom: responsive.spacing['2xl'],
          }}
        >
          <span
            style={{
              flex: 1,
              color: theme.text.primary,
              fontSize: responsive.font.base,
              lineHeight: 1.4,
            }}
          >
            {item.title}
          </span>
          <ForwardChevronIcon />
        </div>
      ))}
    </div>
  );
};
